;(() => {
  const PER_NIGHT = "per-night"
  const TOTAL_STAY = "total-stay"

  // Module-scoped so the display mode, the first capture and the applied price
  // survive a filter change that re-renders the slider, all resetting only on a
  // full page load.
  let viewMode = TOTAL_STAY
  let initialCaptureFired = false
  let priceApplied = false

  stimulusApp.register(
    "budget-filter",
    class extends Stimulus.Controller {
      static targets = [
        "price",
        "rangeDisplay",
        "histogram",
        "bar",
        "track",
        "fill",
        "minHandle",
        "maxHandle",
        "modeToggle",
        "modeLabel",
      ]
      static values = {
        minPrice: Number,
        maxPrice: Number,
        step: Number,
        bucketCount: Number,
        fromIndex: Number,
        toIndex: Number,
        eurMin: Number,
        eurStep: Number,
        nights: Number,
        currencyUnit: String,
        currencyFormat: String,
        rangeFormat: String,
      }
      static activeBarClass = "price-slider--histogram-bar_active"
      static checkedLabelClass = "pseudo-checkbox--toggle--val_checked"
      static draggingClass = "price-slider--histogram_dragging"

      connect() {
        this.section = this.element.closest("details")
        this.section.addEventListener("toggle", this.#captureFirstOpen)
        this.#sync()
        this.#captureFirstOpen()
      }

      disconnect() {
        this.section.removeEventListener("toggle", this.#captureFirstOpen)
        this.#endDrag()
      }

      startDrag(event) {
        event.preventDefault()
        event.stopPropagation()

        this.dragging = event.params.handle
        this.dragStart = [this.minIndex, this.maxIndex]
        this.histogramTarget.classList.add(this.constructor.draggingClass)
        document.addEventListener("pointermove", this.#drag)
        document.addEventListener("pointerup", this.#dropHandle)
        document.addEventListener("pointercancel", this.#dropHandle)
      }

      clickBar(event) {
        if (this.#lastIndex === 0) return

        const index = event.params.index

        if (index < this.minIndex) {
          this.minIndex = index
        } else if (index > this.maxIndex) {
          this.maxIndex = index
        } else if (index - this.minIndex <= this.maxIndex - index) {
          this.minIndex = Math.min(index, this.maxIndex - 1)
        } else {
          this.maxIndex = Math.max(index, this.minIndex + 1)
        }

        this.#render()
        this.#apply()
      }

      // Display-only, so the change must not reach `filters-form#submit`: the
      // filter itself always works with total-stay prices.
      toggleMode(event) {
        event.stopPropagation()

        viewMode = this.modeToggleTarget.checked ? TOTAL_STAY : PER_NIGHT
        this.#renderMode()
        this.#capture()
      }

      // A filter change morphs this element in place instead of re-firing
      // `connect`; only the element's own morph event fires after its whole
      // subtree settled — descendants' events bubble up mid-morph. A morph into
      // `_budget_filter_loading` still reaches this action before Stimulus
      // disconnects the controller.
      resync(event) {
        if (event.target !== this.element || !this.hasPriceTarget) return

        this.#sync()
      }

      #captureFirstOpen = () => {
        if (initialCaptureFired || !this.section.open) return

        this.#capture()
      }

      #drag = (event) => {
        const index = this.#bucketAt(event.clientX)

        if (this.dragging === "min") {
          if (index >= this.maxIndex) return
          this.minIndex = index
        } else {
          if (index <= this.minIndex) return
          this.maxIndex = index
        }

        this.#render()
      }

      #dropHandle = () => {
        const [startMin, startMax] = this.dragStart
        this.#endDrag()
        if (this.minIndex === startMin && this.maxIndex === startMax) return

        this.#apply()
      }

      #endDrag() {
        if (!this.dragging) return

        this.dragging = null
        this.histogramTarget.classList.remove(this.constructor.draggingClass)
        document.removeEventListener("pointermove", this.#drag)
        document.removeEventListener("pointerup", this.#dropHandle)
        document.removeEventListener("pointercancel", this.#dropHandle)
      }

      #bucketAt(clientX) {
        const track = this.trackTarget.getBoundingClientRect()
        const offset = HB.isRTL() ? track.right - clientX : clientX - track.left
        const index = Math.round((offset / track.width) * this.#lastIndex)

        return Math.max(0, Math.min(index, this.#lastIndex))
      }

      // The URL carries EUR, a side at its boundary is omitted, and both sides at
      // their boundaries drop the param entirely — the matrix maximum is the 90th
      // percentile, so sending it explicitly would exclude properties.
      #apply() {
        const minAtBoundary = this.minIndex === 0
        const maxAtBoundary = this.maxIndex === this.#lastIndex
        const min = minAtBoundary ? "" : this.#eurAt(this.minIndex)
        const max = maxAtBoundary ? "" : this.#eurAt(this.maxIndex)

        this.priceTarget.disabled = minAtBoundary && maxAtBoundary
        priceApplied = !this.priceTarget.disabled
        this.priceTarget.value = this.priceTarget.disabled
          ? ""
          : `${min}...${max}`

        this.#capture()
        this.priceTarget.dispatchEvent(new Event("change", { bubbles: true }))
      }

      #sync() {
        const cleared = priceApplied && this.priceTarget.disabled
        priceApplied = !this.priceTarget.disabled
        this.minIndex = this.fromIndexValue
        this.maxIndex = this.toIndexValue

        if (viewMode === PER_NIGHT && this.hasModeToggleTarget)
          this.#renderMode()
        if (cleared && this.section.open) this.#capture()
      }

      #render() {
        const minPosition = this.#position(this.minIndex)
        const maxPosition = this.#position(this.maxIndex)

        this.minHandleTarget.style.setProperty(
          "--position-x",
          `${minPosition}%`,
        )
        this.maxHandleTarget.style.setProperty(
          "--position-x",
          `${maxPosition}%`,
        )
        this.fillTarget.style.setProperty(
          "--track-fill-left",
          `${minPosition}%`,
        )
        this.fillTarget.style.width = `${maxPosition - minPosition}%`
        this.barTargets.forEach((bar, index) => {
          bar.classList.toggle(
            this.constructor.activeBarClass,
            index >= this.minIndex && index <= this.maxIndex,
          )
        })
        this.#renderRange()
      }

      #renderMode() {
        const perNight = viewMode === PER_NIGHT

        // The one element on the page neither a stream nor a morph rewrites, so
        // the server-rendered chip labels pick the mode with CSS alone.
        document.documentElement.dataset.priceViewMode = viewMode
        this.modeToggleTarget.checked = !perNight
        this.modeLabelTargets.forEach((label, index) => {
          label.classList.toggle(
            this.constructor.checkedLabelClass,
            perNight === (index === 0),
          )
        })
        this.#renderRange()
      }

      #renderRange() {
        const suffix = this.maxIndex === this.#lastIndex ? "+" : ""

        this.rangeDisplayTarget.textContent =
          this.rangeFormatValue
            .replace("%{from}", this.#formatPrice(this.#valueAt(this.minIndex)))
            .replace("%{to}", this.#formatPrice(this.#valueAt(this.maxIndex))) +
          suffix
      }

      #capture() {
        initialCaptureFired = true
        Activity.capture("filter price-range", {
          filter: {
            min: this.#valueAt(this.minIndex) / this.#divisor,
            max: this.#valueAt(this.maxIndex) / this.#divisor,
            min_at_first: this.minIndex === 0,
            max_at_last: this.maxIndex === this.#lastIndex,
            currency_unit: this.currencyUnitValue,
            price_view_mode: viewMode,
            nights: this.nightsValue,
          },
        })
      }

      #formatPrice(amount) {
        return accounting.formatMoney(Math.round(amount / this.#divisor), {
          precision: 0,
          symbol: this.currencyUnitValue,
          format: this.currencyFormatValue,
        })
      }

      #position(index) {
        return (100 * index) / this.#lastIndex
      }

      #valueAt(index) {
        return index === this.#lastIndex
          ? this.maxPriceValue
          : this.minPriceValue + index * this.stepValue
      }

      // Built from the exact EUR boundaries and rounded up, never from the
      // display-currency prices: `budget_filter_data` re-derives the selection by
      // flooring this value back into a bucket, and anything below the exact
      // boundary re-renders the selection a bucket short of where the pointer
      // landed. The EUR step is a whole number, so the ceiling stays inside
      // this bucket.
      #eurAt(index) {
        return Math.ceil(this.eurMinValue + index * this.eurStepValue)
      }

      get #divisor() {
        return viewMode === PER_NIGHT ? this.nightsValue : 1
      }

      get #lastIndex() {
        return this.bucketCountValue - 1
      }
    },
  )
})()
